import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Producto } from 'src/app/models/Producto';
import { UsuarioService } from 'src/app/services/usuario.service';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { API_URI } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import * as bootstrap from 'bootstrap';

@Component({
    selector: 'app-my-account-producto',
    templateUrl: './my-account-producto.component.html',
    styleUrls: ['./my-account-producto.component.css'],
    standalone: false
})
export class MyAccountProductoComponent implements OnInit, OnDestroy {
  private _router = inject(Router);
  private _aRoute = inject(ActivatedRoute);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);
  private _sanitizer = inject(DomSanitizer);

  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);

  error = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  id = Number(this._aRoute.snapshot.paramMap.get('id') ?? 0);

  operacion = signal<string>('Agregar ');
  botonOperacion = signal<string>('Agregar ');

  // imagen
  private readonly NO_IMAGE_PLACEHOLDER = './assets/img/no-image.png';
  x_img_producto?: File;
  preview_img_producto = signal<string>('');
  private previewImgProductoOriginal = '';
  private modalInstance?: any;
  private modalConfirmarEliminarInstance?: any;

  // Regex: "123" | "123," | "123,4" | "123,45" | idem con punto
  private readonly DECIMAL_2_REGEX = /^\d+([.,]\d{0,2})?$/;

  // Form: precio y porcentaje como STRING (para no perder el separador al tipear)
  myAccountProducto = this._fb.group({
    nombre: ['', Validators.required],
    descripcion: ['', Validators.required],
    precio: ['', [Validators.required, Validators.pattern(this.DECIMAL_2_REGEX)]],
    porcentaje_descuento: ['', [Validators.required, Validators.pattern(this.DECIMAL_2_REGEX)]],
    sku: ['', Validators.required]
  });

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    // cerrar modal si navega hacia atrás
    this._location.subscribe(() => {
      const modalEl = document.getElementById('modalPreviewImgProducto');
      if (modalEl && this.modalInstance) this.modalInstance.hide();
    });

    await this._loadingService.run(async () => {
      try {
        await this._authService.ensureSession();
      } catch (e: any) {
        console.error(e);
        this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
        this._router.navigate(['/sign-in']);
        return;
      }

      if (this.id && this.id !== 0) {
        this.operacion.set('Editar ');
        this.botonOperacion.set('Actualizar ');

        try {
          const res = await firstValueFrom(this._usuarioService.getProducto(this.id));

          if (!res) {
            // No existe => no tiene sentido editar
            this.error.set('El producto no existe o fue eliminado.');
            this._router.navigate(['/my-account-productos']);
            return;
          }

          this.myAccountProducto.patchValue({
            nombre: res.x_nombre ?? '',
            descripcion: res.x_descripcion ?? '',
            // guardo como string para que sea editable con , o .
            precio: (res.n_precio ?? '').toString(),
            porcentaje_descuento: (res.n_porcentaje_descuento ?? 0).toString(),
            sku: res.x_sku ?? ''
          });

          const img = res.x_img_producto
            ? `${API_URI}uploads/images/${res.x_img_producto}`
            : this.NO_IMAGE_PLACEHOLDER;

          this.preview_img_producto.set(img);
          this.previewImgProductoOriginal = img;
        } catch (e) {
          console.error(e);
          this.error.set('No se pudieron obtener los datos del producto.');
        }
      } else {
        this.preview_img_producto.set('');
      }
    });
  }

  ngOnDestroy(): void {
    if (this.modalInstance) this.modalInstance.hide();
    if (this.modalConfirmarEliminarInstance) this.modalConfirmarEliminarInstance.hide();
  }

  back() {
    this._location.back();
  }

  // ---- Modal ----
  openModal() {
    const modalEl = document.getElementById('modalPreviewImgProducto');
    if (modalEl) {
      this.modalInstance = new (bootstrap as any).Modal(modalEl);
      this.modalInstance.show();
    }
  }

  // -------------------------
  // Helpers decimales
  // -------------------------
  /** deja solo dígitos y un separador decimal (.,) y corta a 2 decimales */
  private cleanDecimalInput(raw: string): string {
    let v = String(raw ?? '');

    // permitir solo 0-9 , .
    v = v.replace(/[^0-9.,]/g, '');

    // si tiene ambos, me quedo con el primero que aparezca
    const firstSepIdx = v.search(/[.,]/);
    if (firstSepIdx !== -1) {
      const sep = v[firstSepIdx]; // '.' o ','
      const left = v.slice(0, firstSepIdx).replace(/[.,]/g, '');
      let right = v.slice(firstSepIdx + 1).replace(/[.,]/g, '');
      right = right.slice(0, 2); // 2 decimales
      v = left + sep + right;
    } else {
      // sin separador: saco cualquier separador sobrante (por si quedó)
      v = v.replace(/[.,]/g, '');
    }

    // no permitir empezar con separador: ",5" => "0,5"
    if (v.startsWith('.') || v.startsWith(',')) v = '0' + v;

    // no permitir ceros raros al inicio tipo "00012" (opcional)
    // v = v.replace(/^0+(\d)/, '$1');

    return v;
  }

  /** normaliza a number: acepta coma o punto */
  private parseDecimalToNumber(raw: string): number | null {
    const s = String(raw ?? '').trim();
    if (!s) return null;

    // permitir "12," o "12." mientras escribe, al guardar lo tomo como 12
    const normalized = s.replace(',', '.').replace(/\.$/, '').replace(/,$/, '');
    if (!normalized) return null;

    const n = Number(normalized);
    return Number.isNaN(n) ? null : n;
  }

  // -------------------------
  // Input handlers
  // -------------------------
  onPrecioInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const cleaned = this.cleanDecimalInput(input.value);

    // IMPORTANT: actualizo el input y el control como STRING (no number)
    input.value = cleaned;
    this.myAccountProducto.controls.precio.setValue(cleaned, { emitEvent: false });
  }

  onDescuentoInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let cleaned = this.cleanDecimalInput(input.value);

    // clamp 0..100 (si ya es parseable)
    const n = this.parseDecimalToNumber(cleaned);
    if (n !== null) {
      const clamped = Math.min(100, Math.max(0, n));
      // si cambió por clamp, vuelvo a string con máximo 2 decimales
      if (clamped !== n) {
        // lo dejo con '.' como string, pero igual acepta coma al tipear
        cleaned = clamped.toFixed(2).replace(/\.00$/, ''); // si querés, sacá este replace y siempre queda 2 dec.
      }
    }

    input.value = cleaned;
    this.myAccountProducto.controls.porcentaje_descuento.setValue(cleaned, { emitEvent: false });
  }

  // ---- Guardar ----
  async saveProducto(): Promise<void> {
    this.error.set('');

    const v = this.myAccountProducto.value;

    if (!v.nombre) { this.error.set('Debe Completar el Nombre'); return; }
    if (!v.descripcion) { this.error.set('Debe Completar la Descripción'); return; }
    if (!v.sku) { this.error.set('Debe Completar el SKU'); return; }

    const precioNum = this.parseDecimalToNumber(v.precio ?? '');
    if (precioNum === null || precioNum <= 0) {
      this.error.set('Debe Completar el Precio (mayor a 0)');
      return;
    }

    const descNum = this.parseDecimalToNumber(v.porcentaje_descuento ?? '');
    if (descNum === null || descNum < 0 || descNum > 100) {
      this.error.set('El % de descuento debe estar entre 0 y 100');
      return;
    }

    if (this.myAccountProducto.invalid) {
      this.myAccountProducto.markAllAsTouched();
      this.error.set('Por favor, completá los campos requeridos.');
      return;
    }

    await this._loadingService.run(async () => {
      const producto: Producto = {
        id_usuario: this._usuarioService.usuario().id,
        x_nombre: v.nombre!,
        x_descripcion: v.descripcion!,
        n_precio: precioNum,
        n_porcentaje_descuento: descNum,
        x_sku: v.sku!
      };

      try {
        if (this.id && this.id !== 0) {
          producto.id = this.id;
          await firstValueFrom(this._usuarioService.updateProducto(producto));
          await this.updateImgProducto(producto.id!);
          await this._router.navigate(['/my-account-productos']);
        } else {
          const res: any = await firstValueFrom(this._usuarioService.createProducto(producto));
          await this.updateImgProducto(res.id);
          await this._router.navigate(['/my-account-productos']);
        }
      } catch (e) {
        console.error(e);
        this.error.set('Error al guardar el Producto');
      }
    });
  }

  private async updateImgProducto(id_producto: number): Promise<void> {
    if (!this.x_img_producto) return;

    try {
      const res: any = await firstValueFrom(this._usuarioService.updateProductoImg(this.x_img_producto, id_producto));
      this.preview_img_producto.set(res?.imagen ? res.imagen : this.preview_img_producto());
    } catch (e) {
      console.error(e);
      this.error.set('Error al actualizar la Imagen del Producto');
    }
  }

  // ---- Foto ----
  private async pickPhoto(fileName: string): Promise<{ file: File; dataUrl: string }> {
    const source = Capacitor.getPlatform() === 'web' ? CameraSource.Photos : CameraSource.Prompt;

    const photo: Photo = await Camera.getPhoto({
      source,
      resultType: CameraResultType.Uri,
      quality: 85,
      allowEditing: false,
      correctOrientation: true,
      promptLabelHeader: 'Seleccionar imagen',
      promptLabelPhoto: 'Galería',
      promptLabelPicture: 'Cámara',
    });

    const file = await this.photoToFile(photo, fileName);
    const dataUrl = await this.fileToDataUrl(file);
    return { file, dataUrl };
  }

  private async photoToFile(photo: Photo, fileName: string): Promise<File> {
    let blob: Blob | null = null;

    if (photo.webPath) {
      const res = await fetch(photo.webPath);
      blob = await res.blob();
    } else if (photo.dataUrl) {
      const res = await fetch(photo.dataUrl);
      blob = await res.blob();
    } else if (photo.base64String) {
      const byteString = atob(photo.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      blob = new Blob([ab], { type: 'image/jpeg' });
    }

    if (!blob) throw new Error('No se pudo leer la imagen.');
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  }

  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  async capturarFileImgProducto(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('producto.jpg');
      this.x_img_producto = file;
      this.preview_img_producto.set(dataUrl);
    } catch (e) {
      console.error('Error al capturar imagen de producto:', e);
    }
  }

  // ---- Eliminar imagen ----
  hayImagenParaEliminar(): boolean {
    const preview = this.preview_img_producto();
    return !!preview && preview !== this.NO_IMAGE_PLACEHOLDER;
  }

  eliminarImgProducto(): void {
    const modalEl = document.getElementById('modalConfirmarEliminarImagenProducto');
    if (modalEl) {
      this.modalConfirmarEliminarInstance = new (bootstrap as any).Modal(modalEl);
      this.modalConfirmarEliminarInstance.show();
    }
  }

  async confirmarEliminarImgProducto(): Promise<void> {
    this.modalConfirmarEliminarInstance?.hide();

    if (this.x_img_producto) {
      // era una imagen recién elegida, todavía no subida al servidor: solo cancelo la selección
      this.x_img_producto = undefined;
      this.preview_img_producto.set(this.previewImgProductoOriginal);
      return;
    }

    if (!this.id || this.id === 0) return;

    await this._loadingService.run(async () => {
      try {
        await firstValueFrom(this._usuarioService.deleteProductoImg(this.id));
        this.previewImgProductoOriginal = this.NO_IMAGE_PLACEHOLDER;
        this.preview_img_producto.set(this.NO_IMAGE_PLACEHOLDER);
      } catch (e) {
        console.error('Error al eliminar la imagen del producto:', e);
        this.error.set('No se pudo eliminar la imagen. Intentá nuevamente.');
      }
    });
  }

  extraerBase64 = async ($event: any) => new Promise((resolve, reject) => {
    try {
      const unsafeImg = window.URL.createObjectURL($event);
      const image = this._sanitizer.bypassSecurityTrustUrl(unsafeImg);
      const reader = new FileReader();
      reader.readAsDataURL($event);
      reader.onload = () => {
        resolve({
          base: reader.result
        });
      };
      reader.onerror = error => {
        resolve({
          base: null
        });
      };
      return;
    } catch (e) {
      return null;
    }
  })
}
